// Pure helpers for the file-tree drag-and-drop graph.
// No DOM imports — everything operates on paths + the same `tree` shape
// produced by /app/api/tree (folders have { type, name, children }; files
// carry a precomputed `path`).

export function basename(p) {
  const i = p.lastIndexOf('/');
  return i < 0 ? p : p.slice(i + 1);
}

export function isDescendantOf(parent, candidate) {
  if (parent === '') return true;
  if (candidate === parent) return true;
  return candidate.startsWith(parent + '/');
}

export function composeDropTarget({ sourcePath, targetFolder }) {
  const name = basename(sourcePath);
  return targetFolder === '' ? name : `${targetFolder}/${name}`;
}

export function isValidDrop({ sourcePath, sourceType, targetFolder }) {
  if (sourceType === 'folder') {
    // Can't drop a folder onto itself or any of its own descendants
    if (isDescendantOf(sourcePath, targetFolder)) {
      return { ok: false, reason: "can't move a folder into itself" };
    }
  }
  // Can't drop onto the immediate parent (no-op)
  const parent = sourcePath.includes('/')
    ? sourcePath.slice(0, sourcePath.lastIndexOf('/'))
    : '';
  if (parent === targetFolder) {
    return { ok: false, reason: 'already in this folder' };
  }
  return { ok: true };
}

function* walkFiles(nodes, prefix) {
  for (const n of nodes) {
    const p = prefix ? `${prefix}/${n.name}` : n.name;
    if (n.type === 'file') yield p;
    else if (n.type === 'folder' && Array.isArray(n.children)) {
      yield* walkFiles(n.children, p);
    }
  }
}

function findFolder(nodes, parts) {
  if (parts.length === 0) return nodes;
  const [head, ...rest] = parts;
  const node = nodes.find((n) => n.type === 'folder' && n.name === head);
  if (!node) return null;
  return findFolder(node.children || [], rest);
}

export function collectFilesUnder(tree, folderPath) {
  const parts = folderPath.split('/').filter(Boolean);
  const children = findFolder(tree, parts);
  if (!children) return [];
  return [...walkFiles(children, folderPath)];
}

export function collectFolders(tree) {
  const folders = [];
  const walk = (nodes, prefix) => {
    for (const n of nodes) {
      if (n.type === 'folder') {
        const p = prefix ? `${prefix}/${n.name}` : n.name;
        folders.push(p);
        if (Array.isArray(n.children)) walk(n.children, p);
      }
    }
  };
  walk(tree, '');
  folders.sort();
  return folders;
}

export function expandFolderMoves({ tree, sourceFolder, targetFolder }) {
  const parts = sourceFolder.split('/').filter(Boolean);
  const children = findFolder(tree, parts);
  if (!children) return [];
  const out = [];
  // sourceFolder's basename becomes the first segment under targetFolder
  const sourceBase = basename(sourceFolder);
  const destPrefix = targetFolder === ''
    ? sourceBase
    : `${targetFolder}/${sourceBase}`;
  for (const filePath of walkFiles(children, sourceFolder)) {
    const tail = filePath.slice(sourceFolder.length + 1); // strip "<sourceFolder>/"
    out.push({
      source: filePath,
      destination: `${destPrefix}/${tail}`,
    });
  }
  return out;
}
